import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { FormGroup } from '@angular/forms';
import { firstValueFrom, startWith } from 'rxjs';
import { CaModalOverlayService, ModalConfig } from '@global-front-components/ui/modal-overlay';

import { PymesCopagosCarenciasComponent } from '../pymes-copagos-carencias/pymes-copagos-carencias.component';
import { PymesObjecionesVentasComponent } from '../pymes-objeciones-ventas/pymes-objeciones-ventas.component';
import { PymesPreguntasFrequentesComponent } from '../pymes-preguntas-frequentes/pymes-preguntas-frequentes.component';
import { PymesBeneficiosSeguroComponent } from '../pymes-beneficios-seguro/pymes-beneficios-seguro.component';
import { AhorroFiscalComponent } from '../ahorro-fiscal/ahorro-fiscal.component';
import { PymesDocumentacionNecesariaComponent } from '../pymes-documentacion-necesaria/pymes-documentacion-necesaria.component';
import { PymesInfoProductoComponent } from '../pymes-info-producto/pymes-info-producto.component';

import { RecomendadorApiService } from 'apps/mnv-seguros-ng/src/app/comun/services/api/recomendador-api.service';
import { ColectivaMapeada, ProtocoloItem, ProtocoloResponse } from '../../../../interfaces/protocolResponse.interface';
import { PymesCanalUsuario } from '../../../../interfaces/canalUsuario.interface';

@Component({
  selector: 'app-pymes-medico-concertado',
  standalone: true,
  imports: [CommonModule, TranslateModule, CaButtonComponent],
  templateUrl: './pymes-medico-concertado.component.html',
  styleUrls: ['./pymes-medico-concertado.component.scss']
})
export class PymesMedicoConcertadoComponent implements OnInit {

  @Input() hospitalization!: boolean;
  @Input({ required: true }) form!: FormGroup;
  @Output() setProtocol = new EventEmitter<number | null>();

  protocol = [2002, 2502, 2302];
  title!: string;
  isShowAllActivated: boolean = false;
  colectivasMapeadas!: ColectivaMapeada[];

  showComponent: boolean = false;
  header: string = null;

  titleObject: {
    title: string;
    infoProducto: string;
    textoPrincipal: string;
    textoSecundario: string;
  }[] = [];

  price: string[] = [];

  priceSuffix = '€/mes';
  tariffText = 'Tarifa única entre 0 y 69 años';

  readonly utilityLinks: string[] = [
    'Calculadora Ahorro Fiscal',
    'Preguntas Frecuentes Seguro Salud Pyme',
    'Objeciones en la venta',
    'Beneficios de contratar un seguro de salud pyme',
    'Cuadro Médico',
    'Documentación necesaria para la contratación',
    '¿Qué son el COPAGO y las CARENCIAS?'
  ];

  pymesCanalUsuario!: PymesCanalUsuario;

  constructor(
    private _modalService: CaModalOverlayService,
    private recomendadorApiService: RecomendadorApiService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.resetCanalUsuario();
    this.listenFormChanges();
    console.log('aquí');
  }

  private listenFormChanges(): void {
    this.form.valueChanges
      .pipe(startWith(this.form.getRawValue()))
      .subscribe(value => {
        this.updateFormState(value);
      });
    console.log('listenFormChanges: ');
  }

  async getPreciosProductos(obj: number[]): Promise<void> {
    this.price = [];

    try {
      const colectivas = await firstValueFrom(
        this.recomendadorApiService.getPrecioProductos(obj)
      );

      this.colectivasMapeadas = colectivas.map(c =>
        this.mapColectiva(c)
      );

      this.price = this.colectivasMapeadas.map(v => v?.prima);

    } catch {
      this.price = ['-'];
    }

    this.showComponent = true;
  }

  async getPrecioProductos(obj: number[]): Promise<void> {
    this.price = [];

    try {
      const colectivas: ProtocoloResponse = await firstValueFrom(
        this.recomendadorApiService.getPrecioProductos(obj)
      );

      this.price.push(
        colectivas.map(c => this.mapColectiva(c))[0].prima
      );

    } catch {
      this.price.push('-');
    }

    this.showComponent = true;
  }

  private mapColectiva(colectiva: ProtocoloItem): ColectivaMapeada {
    const precioTramo = colectiva.precios.find(
      p => p.tramoEdad === '0-69'
    );

    return {
      protocolo: colectiva.protocolo,
      subProtocolo: colectiva.subProtocolo,
      prima: precioTramo?.prima.toString() ?? '-'
    };
  }

  private emitProtocol(protocol: number | null): void {
    if (this.form.get('protocol')?.value === protocol) return;
    this.setProtocol.emit(protocol);
  }

  private setTitleObject(titles: string[] | null): void {
    const map: { [key: string]: string } = {
      '2302': 'Salud Médica Pyme',
      '2502': 'Salud Prestigio Pyme',
      '2002': 'Salud Integral Pyme (Menos de 20 asegurados)',
      '2003': 'Salud Integral Pyme (Más de 20 asegurados)'
    };
    const hiddenSecondaryTitles = new Set(['2002']);

    if (!titles) {
      this.titleObject = [];
      return;
    }

    this.titleObject = titles.map(t => {
      const infoProducto = map[t] || null;
      const match = infoProducto?.match(/\(.*\)$/);

      return {
        title: t,
        infoProducto,
        textoPrincipal: infoProducto?.replace(/\s*\(.*\)$/, '').trim() || '',
        textoSecundario: hiddenSecondaryTitles.has(t) ? '' : (match ? match[0] : '')
      };
    });
  }

  private async updateFormState(value: any): Promise<void> {
    this.showComponent = false;
    this.header = null;

    let protocol: number | null = null;

    if (value['coverage'] === 'sin-hospitalizacion') {
      this.header = 'PYMES.WITHOUT-HOSPITALIZATION';
      this.setTitleObject(['2302']);

      this.form.get('age')?.setValue(null, { emitEvent: false });
      this.form.get('medical')?.setValue(null, { emitEvent: false });

      protocol = 2302;

    } else if (value['coverage'] === 'con-hospitalizacion') {
      if (value['medical'] === 'cuadro-medico-libre') {
        this.header = 'PYMES.FREE-DOCTOR';
        this.setTitleObject(['2502']);

        this.form.get('age')?.setValue(null, { emitEvent: false });
        protocol = 2502;

      } else if (value['medical'] === 'cuadro-medico-concertado') {
        this.header = 'PYMES.DOCTOR';
        this.setTitleObject(['2002']);

        this.form.get('age')?.setValue(null, { emitEvent: false });
        protocol = 2002;
      }
    }

    if (protocol !== null) {
      await this.getPrecioProductos([protocol]);
      await this.getColectivas();
      this.isShowAllActivated = false;
    }

    this.emitProtocol(protocol);
  }

  async showAll() {
    this.showComponent = false;
    this.isShowAllActivated = true;

    await this.getPreciosProductos(this.protocol);

    const validProtocols =
      this.colectivasMapeadas?.map(c => c.protocolo.toString()) || [];

    this.setTitleObject(validProtocols);
  }

  onConsultarInformacion(i: number): void {
    this.openViewPopUp(PymesInfoProductoComponent, {
      infoProducto: this.titleObject[i]?.infoProducto
    }, '90vw', '70vh');
  }


  showCalculator(): void {
    this.openViewPopUp(AhorroFiscalComponent, {
      prima: this.price[0]
    }, '95vw', '95vh');
  }



  openViewPopUp(type: any, data: any = null, width: string = 'auto', height: string = 'auto') {

    const config: ModalConfig = {
      windowDrag: true,
      backdropClick: true,
      isBodyScrollable: true,
      closable: true,
      minimizable: false,
      width,
      height,
      data
    };


    this._modalService.open(type, config);
  }

  navigateToQuestions() {
    this.openViewPopUp(PymesPreguntasFrequentesComponent);
  }

  navigateToSalesObjections() {
    this.openViewPopUp(PymesObjecionesVentasComponent);
  }

  navigateToInsuranceBenefits() {
    this.openViewPopUp(PymesBeneficiosSeguroComponent);
  }

  navigateToMedicalChart() {
    const tipoCanalActivo = Object.values(this.pymesCanalUsuario)
      .find((canal: any) => canal.value === true)
      ?.typeCanal;

    const url = `/70informacion.50cuadromedico`;

    console.log(url);
    window.parent.postMessage({
      cmd: 'imprimir',
      url: url
    }, '*');
  }

  abrirModalInfoCampania() {
    this._modalService.open(PymesCopagosCarenciasComponent, {
      title: '¿Qué son los COPAGOS y las CARENCIAS?'
    });
  }

  abrirDocumentacion() {
    const canalTrue = Object.entries(this.pymesCanalUsuario)
      .find(([_, c]) => c.value)?.[0];
    console.log('caanal: ', canalTrue);
    this.openViewPopUp(PymesDocumentacionNecesariaComponent, {
      canal: canalTrue
    });
  }

  handleUtilityClick(index: number) {
    if (index === 0) this.showCalculator();
    else if (index === 1) this.navigateToQuestions();
    else if (index === 2) this.navigateToSalesObjections();
    else if (index === 3) this.navigateToInsuranceBenefits();
    else if (index === 4) this.navigateToMedicalChart();
    else if (index === 5) this.abrirDocumentacion();
    else if (index === 6) this.abrirModalInfoCampania();
  }

  async getColectivas() {
    try {
      const colectivas: any = await firstValueFrom(
        this.recomendadorApiService.consultarCanalUsuario()
      );
      this.setCanalUsuario(colectivas);
      console.log('canal: ', colectivas)
    } catch {
      console.log('Error colectivas');
    }
  }

  setCanalUsuario(colectivas: string) {
    this.resetCanalUsuario();

    if (colectivas === 'AyC') this.pymesCanalUsuario.AyC.value = true;
    else if (colectivas === 'CJ') this.pymesCanalUsuario.CJ.value = true;
    else if (colectivas === 'GC') this.pymesCanalUsuario.GC.value = true;
    else if (colectivas === 'HE') this.pymesCanalUsuario.HE.value = true;
  }

  resetCanalUsuario() {
    this.pymesCanalUsuario = {

      AyC: {
        value: false,
        url: this.translateService.instant('PYMES.CANAL-USUARIO.AyC'),
        typeCanal: this.translateService.instant('PYMES.CANAL-USUARIO.TYPE-AyC')
      },
      CJ: {
        value: false,
        url: this.translateService.instant('PYMES.CANAL-USUARIO.CJ'),
        typeCanal: this.translateService.instant('PYMES.CANAL-USUARIO.TYPE-CJ')
      },
      GC: {
        value: false,
        url: this.translateService.instant('PYMES.CANAL-USUARIO.GC'),
        typeCanal: this.translateService.instant('PYMES.CANAL-USUARIO.TYPE-GC')
      },
      HE: {
        value: false,
        url: this.translateService.instant('PYMES.CANAL-USUARIO.HE'),
        typeCanal: this.translateService.instant('PYMES.CANAL-USUARIO.TYPE-HE')
      },
    };
  }

  getCanalUsuario(item: number) {
    let url: string = '';

    if (this.isShowAllActivated) {
      const tipo = this.colectivasMapeadas[item];
      url = `/web/agentes/10productos.20personales.161colectivosalud.colectivos${tipo.protocolo}`;
    } else {
      const protocolo = this.form.get('protocol')?.value;
      url = `/web/agentes/10productos.20personales.161colectivosalud.colectivos${protocolo}`;
    }
    console.log('URL: ', url);
  }

  onImprimir(item: number): void {
    let protocol = this.form.get('protocol')?.value;

    const tipoCanalActivo: string = Object.values(this.pymesCanalUsuario)
      .find((canal: any) => canal.value === true)
      ?.typeCanal;

    if (this.isShowAllActivated) {
      protocol = this.protocol[item];
    }

    if (!protocol) {
      console.log('Error obteniendo el protocolo');
      return
    }

    if (!tipoCanalActivo) {
      console.error('Error obteniendo el tipo de canal');
      return;
    }

    const basePath = `10productos.20personales.161colectivosalud.colectivos.documento${protocol}`;

    const url: string = tipoCanalActivo === 'agentes'
      ? `/${basePath}`
      : `/web/${tipoCanalActivo}/${basePath}`;

    console.log('onImprimir- url:', url);

    window.parent.postMessage({
      cmd: 'imprimir',
      url: url
    }, '*');
  }

  private checkLabel(title: string) {
    this.title = title;
    return this.title !== 'Salud Médica Pyme' ? true : false;
  }

  onContratar(item: number) {
    const url = Object.values(this.pymesCanalUsuario)
      .find((canal: any) => canal.value === true)
      ?.url;

    let protocolo = this.form.get('protocol')?.value;

    if (this.isShowAllActivated)
      protocolo = this.protocol[item];

    console.log('onContratar - URL: ', url);
    console.log('onContratar - protocolo: ', protocolo);

    window.parent.postMessage({
      cmd: 'contratar',
      url: url,
      params: {
        cod_producto: protocolo
      }
    }, '*');
  }
}
